import stylesheet from './CustomSelect.scss?inline';
import {NXTInputField} from "./InputField";
import {NXTTag} from "../Tag/Tag";

export class NXTCustomSelectInput extends NXTInputField<string[]> {
    customDropdown: HTMLElement;
    customSelect: HTMLElement;
    selectionCounter: NXTTag;
    nativeSelect: HTMLSelectElement | null = null;

    constructor() {
        super();
        this.customStyle = stylesheet;
    }

    get icon(): string {
        return this.getAttribute('icon');
    }

    get label(): string {
        return this.getAttribute('label') ?? '';
    }

    get multiple(): boolean {
        return this.hasAttribute('multiple') || !!this.nativeSelect?.multiple;
    }

    private getSlottedSelect(): HTMLSelectElement | null {
        const selects = [...this.getSlotElements('select'), ...this.getSlotElements('')].filter(
            (el): el is HTMLSelectElement => el instanceof HTMLSelectElement,
        );
        return selects[0] ?? null;
    }

    private get sourceOptions(): HTMLOptionElement[] {
        if (this.nativeSelect) {
            return Array.from(this.nativeSelect.options);
        }
        return this.getSlotElements('').filter(
            (el): el is HTMLOptionElement => el instanceof HTMLOptionElement,
        );
    }

    get labelType(): string {
        return this.getAttribute('label-type') ?? '';
    }

    get isOpen(): boolean {
        return this.customDropdown.classList.contains('open');
    }

    get count(): number {
        return this.value.length;
    }

    get size(): string {
        return this.getAttribute('size') ?? 'md';
    }

    getSizeVariants(): string[] {
        const additionalClasses: string[] = [];
        additionalClasses.push(`custom-select-${this.size}`);
        for (const breakpoint of ['md', 'lg', 'xl', 'xxl']) {
            const attributeName = `size-${breakpoint}`;
            if (this.hasAttribute(attributeName)) {
                additionalClasses.push(`custom-select-${this.getAttribute(attributeName)}-${breakpoint}-up`);
            }
        }
        return additionalClasses;
    }

    updateLabel() {
        const labels = this.selectedLabels;
        let text: string;
        if (!this.multiple) {
            text = labels[0] ?? this.label;
        } else if (this.labelType === 'selection') {
            if (labels.length === 0) {
                text = this.label;
            } else if (labels.length === 1) {
                text = labels[0];
            } else {
                text = `${labels[0]} + ${labels.length - 1}`;
            }
        } else {
            text = this.label;
        }
        this.customSelect.querySelector('.label').textContent = text;
    }

    repositionDropdown() {
        let rect = this.customDropdown.getBoundingClientRect();
        const offset = Number.parseInt(window.getComputedStyle(this.customDropdown).getPropertyValue('--offset'));
        // If overflows right screen side -> move to left
        if (rect.right > (window.innerWidth - offset)) {
            this.customDropdown.style.left = `auto`;
            this.customDropdown.style.right = `0`;
        }
    }

    createCustomOptionFromOption(option: HTMLOptionElement): HTMLElement {
        const customOption = document.createElement('span');
        customOption.classList.add('option');
        if (option.selected) {
            customOption.classList.add('selected');
        }
        customOption.title = option.textContent;
        customOption.innerHTML = `<span class="entry"><span class="label">${option.textContent}</span></span>`;
        customOption.dataset.value = option.value;
        return customOption;
    }

    updateDropdownItems() {
        this.customDropdown.innerHTML = ``;
        this.sourceOptions.forEach((option: HTMLOptionElement) => {
            this.customDropdown.appendChild(this.createCustomOptionFromOption(option));
        });
    }

    private syncNativeSelectFromCustom() {
        if (!this.nativeSelect) {
            return;
        }
        const selected = this.customSelectedValues;
        Array.from(this.nativeSelect.options).forEach((option) => {
            option.selected = selected.includes(option.value);
        });
        this.nativeSelect.dispatchEvent(new Event('change', {bubbles: true}));
    }

    setSelected(value: string, selected: boolean) {
        const selectedCustomOption = this.customDropdown.querySelector(`.option[data-value="${value}"]`)
        if (!this.multiple) {
            this.close();
            this.customDropdown.querySelectorAll<HTMLElement>('.option').forEach(customOption => {
                customOption.classList.remove('selected');
            })
        }
        if (selectedCustomOption) {
            selectedCustomOption.classList.toggle('selected', selected);
        }
    }

    onClickCustomOption(customOption: HTMLElement) {
        if (!customOption) {
            return;
        }
        this.setSelected(customOption.dataset.value, !customOption.classList.contains('selected'));
        this.syncNativeSelectFromCustom();
        this.dispatchEvent(new Event('change'));
    }

    get selectedLabels(): string[] {
        return Array.from(
            this.customDropdown.querySelectorAll<HTMLElement>('.option.selected'),
            el => el.querySelector('.label').textContent,
        );
    }

    updateCounter() {
        // The tag-counter is shown for multi-select by default; hidden for single-select
        // and for label-type="selection" (which shows the values in the trigger instead).
        if (!this.multiple || this.labelType === 'selection') {
            this.selectionCounter.style.display = 'none';
            return;
        }
        this.selectionCounter.querySelector('.count').textContent = this.count.toString();
        this.selectionCounter.style.display = this.count > 0 ? 'inline-block' : 'none';
    }

    protected renderControl(): HTMLElement {
        this.nativeSelect = this.getSlottedSelect();
        if (this.nativeSelect && !this.multiple && !this.nativeSelect.querySelector('option[selected]')) {
            this.nativeSelect.selectedIndex = -1;
        }

        // Container Frame
        const container = document.createElement('div');
        container.classList.add('custom-select', ...this.getSizeVariants())
        if (this.multiple) {
            container.classList.add('select-multiple');
        }
        // (Visible) Custom Select element
        this.customSelect = document.createElement('div');
        this.customSelect.classList.add('custom-input');
        this.customSelect.ariaHidden = "true";
        this.customSelect.innerHTML = `
            <div class="selection-counter">
                <nxt-tag size="sm">
                    <span class="count"></span>
                    <nxt-icon icon="close"></nxt-icon>
                </nxt-tag>
            </div>
            <span class="label">${this.label}</span>
        `;
        this.customSelect.addEventListener('click', () => {
            this.toggle();
        })
        this.selectionCounter = this.customSelect.querySelector('.selection-counter');
        this.selectionCounter.addEventListener('click', (ev) => {
            ev.stopPropagation();
            this.value = [];
        });
        // Dropdown
        this.customDropdown = document.createElement('div');
        this.customDropdown.classList.add('dropdown');
        this.customDropdown.ariaHidden = "true";
        this.customDropdown.addEventListener('click', (ev) => {
            this.onClickCustomOption((ev.target as HTMLElement).closest('.option'));
        })
        this.updateDropdownItems();
        // Add them to the container
        container.appendChild(this.customSelect);
        container.appendChild(this.customDropdown);
        // Project the (visually hidden) native select so it participates in forms.
        if (this.nativeSelect) {
            const selectSlot = document.createElement('slot');
            const slotName = this.nativeSelect.getAttribute('slot');
            if (slotName) {
                selectSlot.setAttribute('name', slotName);
            }
            container.appendChild(selectSlot);
        }
        // Global "outside click" listener
        addEventListener('click', (ev) => {
            if (!container.contains(ev.target as HTMLElement) && ev.target !== this) {
                this.close();
            }
        });
        // Update Counter automatically
        this.addEventListener('change', () => {
            this.updateCounter();
            this.updateLabel();
        });
        this.updateCounter();
        this.updateLabel();
        // Update Options when content changes
        const observer = new MutationObserver(() => {
            const prevValues = this.value;
            this.updateDropdownItems();
            this.setValue(prevValues);
        });
        observer.observe(this, {
            subtree: true,
            childList: true,
        });
        // Return complete container
        return container;
    }

    toggle() {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    open() {
        this.repositionDropdown();
        this.customDropdown.classList.add('open');
        this.dispatchEvent(new CustomEvent('open'));
    }

    close() {
        this.customDropdown.classList.remove('open');
        this.dispatchEvent(new CustomEvent('close'));
    }

    private get customSelectedValues(): string[] {
        return Array.from(this.customDropdown.querySelectorAll<HTMLElement>('.option.selected'), el => el.dataset.value);
    }

    public get value(): string[] {
        if (this.nativeSelect) {
            return Array.from(this.nativeSelect.selectedOptions, option => option.value);
        }
        return this.customSelectedValues;
    }

    private setValue(value: string[]) {
        this.customDropdown.querySelectorAll<HTMLElement>(`.option`).forEach((customOption) => {
            customOption.classList.toggle('selected', value.includes(customOption.dataset.value));
        });
        this.syncNativeSelectFromCustom();
    }

    public set value(value: string[]) {
        this.setValue(value);
        this.dispatchEvent(new Event('change'));
    }

}

customElements.define('nxt-custom-select', NXTCustomSelectInput);
